import type { ScopeRef } from "@enduring-systems/entitlements";
import { useRef, useState } from "react";

import { ApiError } from "@/lib/api-client";

import {
  alreadyDecided,
  approveRequest,
  type ApproveRequestResult,
  type DecidedRequestView,
  denyRequest,
  type PendingRequestView,
  type RequestDecision,
  type RequesterIdentity,
  type RequestStatus,
} from "../entitlements-api";
import { GRANT_PERMISSION, offeringFailureCopy, offeringWriteBlock } from "./offering-mutation";

/**
 * Answering a queued request, apart from any component that draws it.
 *
 * Deliberately a sibling of `offering-mutation` rather than a fork of it. An
 * approval IS the bundle apply that module performs, reached through a
 * different endpoint, so everything the two share (who may write at a scope,
 * how a bundle failure reads) is delegated to it. An operator must not be told
 * two different things about one write depending on which panel they started
 * from.
 *
 * What is NOT shared is the request plane's own vocabulary: a request can
 * vanish, and it can be answered by a peer between the queue read and the
 * press. Those have no analogue on the card, and they are worded here.
 */

/** The customer-visible copy a denial requires, or null when there is none. */
export function normalizeDenialReason(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

export const DENIAL_REASON_REQUIRED =
  "A denial needs a reason. It is shown to the requester exactly as written, so it has to stand on its own.";

/**
 * Why this operator cannot answer this request at this scope, or null when
 * they can.
 *
 * An APPROVAL classifies exactly like a direct grant write, because it is one:
 * a platform or enterprise-scope approval lands on every tenant or a whole
 * customer org, so it demands the elevated key on top. A DENIAL changes no
 * grants at any scope and stays on the day-to-day key, which mirrors the BFF,
 * where the elevation claim is minted for approvals only.
 *
 * The scope is the REQUEST's, never the page's. The queue is platform-wide, so
 * an operator can be looking at one workspace while answering a request filed
 * against another, and it is the target of the write that decides the key.
 */
export function decisionWriteBlock(
  decision: RequestDecision,
  scope: ScopeRef,
  permissions: readonly string[]
): string | null {
  if (decision === "approve") return offeringWriteBlock(scope, permissions);
  if (permissions.includes(GRANT_PERMISSION)) return null;
  return `Denying a request is exercising the same decision authority as granting. You are missing the ${GRANT_PERMISSION} permission.`;
}

/**
 * What to tell the operator when a decision did not land.
 *
 * The bundle codes are delegated verbatim to the offering card's wording: an
 * approval performs that apply, and two different sentences about one failure
 * is two different accounts of what happened.
 *
 * The request-plane codes are worded here, and every one is sorted on the same
 * line the card's copy is: whether the request could have reached a grant.
 * `UNKNOWN_REQUEST` and a reason rejected at the boundary certainly did not.
 * A 500 is unknown for the approval's reason rather than the card's: the
 * transition and the grant rows commit in ONE transaction, but a lambda-invoke
 * transport failure can land after that commit, so "nothing changed" would be
 * a guess about a privileged write.
 */
export function requestFailureCopy(decision: RequestDecision, cause: unknown): string {
  const verb = decision === "approve" ? "approve" : "deny";
  if (!(cause instanceof ApiError)) {
    return `Could not ${verb} this request, and the outcome is not known. Re-read the queue before trying again.`;
  }

  switch (cause.code) {
    case "UNKNOWN_REQUEST":
      return `This request no longer exists, so there was nothing to ${verb}. It was most likely removed after the queue was read; re-read the queue.`;
    case "UNREADABLE_REQUEST":
      return `This request row cannot be read and needs manual repair, so it cannot be answered from here. Nothing changed.`;
    case "INVALID_REASON":
      return `${DENIAL_REASON_REQUIRED} Nothing changed.`;
    case "OFFERING_NOT_REQUESTABLE":
      return `The offering this request names is no longer requestable, so the approval was refused. Nothing changed at the requester's scope.`;
    case "OFFERING_NOT_HELD":
      return `This is a quota increase on an offering the scope does not hold, so there is nothing to increase. Nothing changed.`;
    case "OFFERING_VERSION_MISMATCH":
      return `The scope holds a different version of this offering than the request assumed, so the increase was refused. Re-read the scope: the request was made against a version that is no longer applied.`;
    default:
      break;
  }
  /* An APPROVAL is the bundle apply, so everything left is the card's to word
     and the two surfaces agree by construction. */
  if (decision === "approve") return offeringFailureCopy("apply", cause);

  /* A DENIAL is not, and must not borrow that wording. `offeringFailureCopy`
     interpolates its verb into every generic branch, so delegating here told
     an operator their "apply" had failed and sent them to re-read a scope a
     denial never touches. A denial writes no grant at any scope, which is
     also why every branch below can say so as a fact. */
  if (cause.status === 403) {
    return `You do not hold the permission a denial needs at this scope, so nothing changed. ${cause.message}`;
  }
  if (cause.status === 400 || cause.status === 422) {
    return `The service refused this denial: ${cause.message}. Nothing changed.`;
  }
  if (cause.status === 503) {
    return `The service could not authorize this denial right now, so nothing changed. Try again shortly.`;
  }
  /* A 500 covers a store-side throw and a transport failure that can land
     after the row was already written, and the client cannot tell those apart,
     so this names the uncertainty rather than guessing. */
  return `Could not deny this request, and the outcome is not known. Re-read the queue before trying again.`;
}

/**
 * The last thing that happened, in the operator's words.
 *
 * Three kinds, not two, because "a peer got there first" is neither a success
 * nor a failure. Rendering it as an error would raise an alarm over the
 * routine outcome of two people working one queue.
 */
export type DecisionOutcome =
  | {
      readonly kind: "decided";
      readonly requestId: string;
      readonly text: string;
      /** The service's own answer, which is what the row is drawn from. */
      readonly request: DecidedRequestView;
      /**
       * Carried from the queue row that was acted on (ENR-2246). The decision
       * routes answer with `requesterId` and no identity, and resolving again
       * would be a second name disclosure for a row whose requester the
       * operator was just shown.
       */
      readonly requester: RequesterIdentity;
    }
  | { readonly kind: "already_handled"; readonly requestId: string; readonly text: string }
  | { readonly kind: "failed"; readonly requestId: string; readonly text: string };

export interface UseRequestDecisionInput {
  /**
   * Fired when the queue has moved, so the page re-reads it and the scope
   * beneath it. Fired for a peer's decision too: that request is no longer
   * pending, and leaving it on screen invites a second operator to try it.
   */
  readonly onDecided: () => void;
  /**
   * The current operator's Cognito sub, for telling "you did this" from
   * "someone else did" on a lost race.
   *
   * Optional, and absence is not treated as "someone else": with no identity
   * to compare against, claiming a peer decided it could be false, so the
   * wording stays neutral. See `handledElsewhereText`.
   */
  readonly operatorId?: string;
}

export interface UseRequestDecisionReturn {
  /** The request currently being answered, or null. */
  readonly pending: { readonly requestId: string; readonly decision: RequestDecision } | null;
  readonly outcome: DecisionOutcome | null;
  /**
   * Settles when the decision has, so a confirmation can stay up until then.
   * Never rejects: a failure is reported through `outcome`, and a caller
   * awaiting this must not have to catch as well.
   */
  readonly run: (
    decision: RequestDecision,
    request: PendingRequestView,
    reason?: string
  ) => Promise<void>;
}

function approvalText(request: DecidedRequestView, apply: ApproveRequestResult["apply"]): string {
  const noun = apply.changes.length === 1 ? "member row" : "member rows";
  return `Approved and applied ${apply.bundleName}@${apply.version ?? request.change.offering.version}: ${String(apply.changes.length)} ${noun} written. The request is now ${request.status}.`;
}

/**
 * What to say when a decision arrived after the request had already been
 * answered.
 *
 * Deliberately never renders `decidedBy` itself. It is a Cognito sub, and
 * "already handled by 8f3a-2b41" is truthful and useless: an operator cannot
 * act on a UUID, and this console has no staff-id-to-name resolution path to
 * turn it into one. What IS actionable is whether it was THEM: a 409 on your
 * own decision is a double-click, a 409 on a peer's means you were raced and
 * the row needs re-reading. Those call for different next actions, and the id
 * separates them without naming anyone.
 *
 * With no operator identity to compare against, the wording stays neutral
 * rather than claiming a peer, because it may have been this operator.
 */
function handledElsewhereText(
  status: RequestStatus | null,
  decidedBy: string | null,
  operatorId: string | undefined
): string {
  const now = status === null ? "" : `, now ${status}`;
  const tail = `, so nothing you pressed was applied. The queue has been re-read.`;
  if (decidedBy !== null && operatorId !== undefined && operatorId !== "") {
    return decidedBy === operatorId
      ? `You already handled this${now}${tail}`
      : `Already handled by another operator${now}${tail}`;
  }
  return `Already handled${now}${tail}`;
}

/**
 * Answer one queued request.
 *
 * Nothing here is optimistic. `onDecided` re-reads the queue and the scope
 * beneath it, and the offering card re-derives from that read, so what the
 * operator sees afterwards is the engine's answer rather than what this hook
 * believes it wrote. The one thing drawn from this hook's own state is the
 * DECIDED row, and that is the service's response object rather than a local
 * edit of the queue: a decided request leaves the pending queue, so the read
 * that follows cannot show what just happened to it.
 */
export function useRequestDecision({
  onDecided,
  operatorId,
}: UseRequestDecisionInput): UseRequestDecisionReturn {
  /** Held for the duration of a decision, so a second cannot start beside it. */
  const inFlight = useRef(false);
  const [pending, setPending] = useState<UseRequestDecisionReturn["pending"]>(null);
  const [outcome, setOutcome] = useState<DecisionOutcome | null>(null);

  const run = async (
    decision: RequestDecision,
    request: PendingRequestView,
    reason?: string
  ): Promise<void> => {
    /* Checked before the guard below so a missing reason cannot consume the
       single in-flight slot; the operator gets the message and the field back. */
    const denialReason = decision === "deny" ? normalizeDenialReason(reason ?? "") : null;
    if (decision === "deny" && denialReason === null) {
      setOutcome({ kind: "failed", requestId: request.id, text: DENIAL_REASON_REQUIRED });
      return;
    }

    /* One decision at a time, gated on a REF rather than on `pending`. A
       second started before the first settles would report its outcome over
       one the operator has not read yet, and on a privileged write it would
       also double the audit rows and the wide-scope alarm. State cannot hold
       that line: the guard would be read from the render that created this
       closure, so anything firing before `setPending` commits sees the stale
       value. A ref is written synchronously and closes the window whatever the
       commit timing. */
    if (inFlight.current) return;
    inFlight.current = true;
    setPending({ requestId: request.id, decision });
    setOutcome(null);

    const call: Promise<DecisionOutcome> =
      decision === "approve"
        ? approveRequest({ scope: request.scope, requestId: request.id }).then((result) => ({
            kind: "decided" as const,
            requestId: request.id,
            text: approvalText(result.request, result.apply),
            request: result.request,
            requester: request.requester,
          }))
        : denyRequest({
            scope: request.scope,
            requestId: request.id,
            reason: denialReason ?? "",
          }).then((denied) => ({
            kind: "decided" as const,
            requestId: request.id,
            text: `Denied. The requester will be shown the reason as written. The request is now ${denied.status}.`,
            request: denied,
            requester: request.requester,
          }));

    await call
      .then((settled) => {
        setOutcome(settled);
        onDecided();
      })
      .catch((cause: unknown) => {
        const peer = alreadyDecided(cause);
        if (peer !== null) {
          setOutcome({
            kind: "already_handled",
            requestId: request.id,
            text: handledElsewhereText(peer.status, peer.decidedBy, operatorId),
          });
          onDecided();
          return;
        }
        setOutcome({
          kind: "failed",
          requestId: request.id,
          text: requestFailureCopy(decision, cause),
        });
        /* A VANISHED request has left the queue just as surely as a decided
           one, so the read has to move. Without this the copy tells the
           operator the row is gone and to re-read, while the row stays on
           screen with its controls live, inviting clicks that keep 404ing.
           Every other failure leaves the request pending and the list correct,
           so only this one refreshes. */
        if (cause instanceof ApiError && cause.code === "UNKNOWN_REQUEST") {
          onDecided();
        }
      })
      .finally(() => {
        inFlight.current = false;
        setPending(null);
      });
  };

  return { pending, outcome, run };
}
